import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, viewChild, ViewChild, ViewContainerRef } from '@angular/core';
import { Platform, ToastController } from '@ionic/angular';
import { StatusBar } from '@awesome-cordova-plugins/status-bar/ngx';
import { ThemeSwitcherService } from './shared/services/theme-switcher/theme-switcher.service';
import { EventsService } from './shared/services/events/events.service';
import { SmartAudioService } from './shared/services/smart-audio/smart-audio.service';
import { NavigateFromLinkService } from './game/navigate-from-link.service';
import { DynamicContentService } from './shared/services/dynamic-content/dynamic-content.service';
import { DynamicLinkService } from './core/firebase/dynamic-link.service';
import { AuthStoreDispatcher } from './core/state/auth/auth.dispatcher';
import { combineLatest, filter, map, Observable, startWith, Subscription, take } from 'rxjs';
import { initNavigationBar } from './plugins/sopo-navigation-bar.plugin';
import { GameStoreDispatcher } from './core/state/game/game.dispatcher';
import { SopoPopupService } from './sopo-popup/service/sopo-popup.service';
import { ZendeskService } from './shared/services/zendesk/zendesk.service';
import { ProfileStateDispatcher } from './core/state/profile/profile.dispatcher';
import { ResizeService } from './shared/services/resize/resize.service';
import { SentryService } from './shared/services/sentry.service';
import { VerificationService } from './verification-page/verification.service';
import { IonIconsService } from './shared/services/ion-icons/ion-icons.service';
import { setLogLevel, LogLevel } from "@angular/fire";
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NetworkService } from './core/network/network.service';
import { AppStateService } from './shared/services/app-state/app-state.service';
import { isElectron, isNativePlatform } from './shared/utils/platform.utils';
import SplashScreenElectron from './plugins/splash-screen.plugin';
import { UpdateService } from './shared/services/update/update.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { SopoTooltipService } from './shared/services/sopoTooltip/sopo-tooltip.service';
import { RuntimeLoader } from '@rive-app/canvas-lite';
import { GameTabManager } from 'src/app/shared/services/game-tab-manager/game-tab-manager.interface';
import { TwitterPixelService } from './shared/services/twitter-pixel/twitter-pixel.service';
import { SopoCheatService } from './sopo-cheat.service';

setLogLevel(LogLevel.SILENT);

@Component({
    selector: 'app-root',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: 'app.component.html',
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('reCaptcha', {read: ViewContainerRef }) reCaptcha: ViewContainerRef;
    @ViewChild('certificatePreview', {read: ViewContainerRef }) certificatePreview: ViewContainerRef;
    @ViewChild('appContainer', { read: ViewContainerRef, static: true }) appContainerRef: ViewContainerRef;
    @ViewChild('sweepbuckMenu', {read: ViewContainerRef }) sweepbuckMenu: ViewContainerRef;
    @ViewChild('shopProduct', {read: ViewContainerRef }) shopProduct: ViewContainerRef;
    @ViewChild('sopoTransition', {read: ViewContainerRef }) sopoTransition: ViewContainerRef;
    @ViewChild('abstractLogin', {read: ViewContainerRef }) abstractLogin: ViewContainerRef;
    sopoTooltipViewContainerRef = viewChild('sopoTooltip', { read: ViewContainerRef });
    @ViewChild('profileSetup', {read: ViewContainerRef }) profileSetup: ViewContainerRef;
    public footerIsHidden = false;
    keyboardOpened = false;
    showHeader = false;
    resumeSubscription: Subscription;
    showHeaderSubscription: Subscription;
    sopoPopupFontSize$: Observable<string>;
    commonFontSize = toSignal(this.resizeService.getCommonFontSize());

    constructor(
        private platform: Platform,
        private statusBar: StatusBar,
        private themeSwitcher: ThemeSwitcherService,
        public events: EventsService,
        private smartAudioService: SmartAudioService,
        private activatedRoute: ActivatedRoute,
        private navigationFromLink: NavigateFromLinkService,
        private dynamicContentService: DynamicContentService,
        private dynamicLinkService: DynamicLinkService,
        private authDispatcher: AuthStoreDispatcher,
        private gameDispatcher: GameStoreDispatcher,
        private sopoPopupService: SopoPopupService,
        private ref: ChangeDetectorRef,
        private zendeskService: ZendeskService,
        private profileDispatch: ProfileStateDispatcher,
        private resizeService: ResizeService,
        private sentryService: SentryService,
        verificationService: VerificationService, // creating VerificationService to check email and phone verification when app is created DO NOT REMOVE!
        private ionIconsService: IonIconsService,
        private networkService: NetworkService,
        sopoCheatService: SopoCheatService, // Initialize sopo cheat service. DO NOT REMOVE!
        appStateService: AppStateService, // Initialize app state service. DO NOT REMOVE!
        private toastController: ToastController,
        private sopoTooltipService: SopoTooltipService,
        private gameTabManager: GameTabManager,
        private twitterPixelService: TwitterPixelService,
        private router: Router,
        private updateService: UpdateService,
    ) {
        this.listenForLinkOpenings();
        this.initializeApp();
        this.themeSwitcher.setTheme('day');
        window.addEventListener('keyboardWillShow', () => {
            if (!this.keyboardOpened) {
                this.forMobileDevices()
            };
            this.keyboardOpened = true;
        });
        window.addEventListener('keyboardWillHide', () => {
            this.keyboardOpened = false;
            this.forMobileDevices();
        });
        this.ionIconsService.initIonIcons();
        this.networkService.initialize();
    }

    ngOnInit(): void {
        RuntimeLoader.setWasmUrl('assets/rive/rive.wasm');
        this.sopoTooltipService.tooltipViewContainerRef = this.sopoTooltipViewContainerRef();
        this.smartAudioService.load();
        const routeUrl$ = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            map((event: NavigationEnd) => event.urlAfterRedirects),
            startWith(this.router.url),
        );
        this.showHeaderSubscription = combineLatest([this.authDispatcher.getState(), this.profileDispatch.getProfileState(), routeUrl$])
            .pipe(
                map(([authState, profileState, url]) => {
                    if (url.startsWith('/coinflow-checkout')) return false;

                    let isGuest = false;
                    if (authState.userData) isGuest = authState.userData.isAnonymous;
                    const isAuthenticated = authState.isAuthenticated;
                    const termsAccepted = profileState.profileData.termsAccepted;
                    const hasWeakPassword = authState.hasWeakPassword ? authState.hasWeakPassword : false;

                    return !isGuest && isAuthenticated && termsAccepted && !hasWeakPassword;
                }))
            .subscribe(result => {
                this.showHeader = result;
                this.ref.detectChanges();
            });
        this.sopoPopupService.setViewContainerRef('app', this.appContainerRef);
        this.sopoPopupFontSize$ = this.resizeService.getSopoPopupFontSize();
        this.initializeSentry();
    }

    private async initializeSentry(): Promise<void> {
        await this.sentryService.initSentry();
        this.twitterPixelService.load();
    }

    initializeApp() {
        if (!isNativePlatform()) {
            return;
        }
        this.forMobileDevices();
        this.events.subscribe('hideHeader', (data) => {
            this.footerIsHidden = data.isHidden;
        });
    }

    ngAfterViewInit(): void {
        this.toastController.create({ message: '', duration: 1 }).then(toast => toast.dismiss());
        this.dynamicContentService.init(this.reCaptcha, this.certificatePreview, this.sweepbuckMenu, this.shopProduct, this.sopoTransition, this.abstractLogin, this.profileSetup);
        this.dynamicContentService.openreCaptcha('recaptcha-container-id');
        this.zendeskService.initialize();
        if (isElectron()) {
            this.router.events.pipe(
                filter(event => event instanceof NavigationEnd),
                take(1),
            ).subscribe(() => {
                this.dismissSplash();
            });
        } else {
            this.handleWebSplash();
        }
    }

    private handleWebSplash(): void {
        const navigationReady = new Promise<void>(resolve => {
            this.router.events.pipe(
                filter(event => event instanceof NavigationEnd),
                take(1),
            ).subscribe(() => resolve());
        });

        const updateCheck = this.updateService.checkForSplashUpdate();

        Promise.all([navigationReady, updateCheck]).then(([, hasUpdate]) => {
            if (!hasUpdate) {
                this.dismissSplash();
            }
        });
    }

    private dismissSplash(): void {
        SplashScreenElectron.hide();
    }

    forMobileDevices() {
        if (this.platform.is('android') || this.platform.is('ios')) {
            this.platform.ready().then(() => {
                this.statusBar.styleDefault();
                this.statusBar.hide();
                if (this.platform.is('android')) {
                    if (this.resumeSubscription) this.resumeSubscription.unsubscribe();
                    this.resumeSubscription = this.platform.resume.subscribe(() => {
                        initNavigationBar();
                    });
                    if (this.keyboardOpened) {
                        this.statusBar.overlaysWebView(true);
                        this.statusBar.show();
                    } else {
                        this.statusBar.overlaysWebView(false);
                        initNavigationBar();
                    }
                }
            });
        }
    }

    listenForLinkOpenings() {
        this.activatedRoute.queryParams.subscribe(async params => {
            if (params.quickplayTableId) {
                const tableInClub = await this.navigationFromLink.checkIfTableIsAlreadyInClub(params.quickplayTableId);
                if (!tableInClub) await this.navigationFromLink.navigateToTable(params.quickplayTableId);
            }
            if (params.quickplayTournamentId) {
                await this.navigationFromLink.navigateToRegPage(params.quickplayTournamentId);
            }
            if (params.mode === 'resetPassword') {
                this.dynamicLinkService.handlePasswordResetAction(params.oobCode);
            }
            if (params.mode === 'verifyEmail') {
                this.dynamicLinkService.handleVerifyEmailAction(params.oobCode);
            }
            if (params.inviteCode) {
                this.dynamicLinkService.handleInviteLink(params.inviteCode);
            }
        });
    }

    ngOnDestroy(): void {
        if (this.resumeSubscription){
            this.resumeSubscription.unsubscribe();
        }
        if(this.showHeaderSubscription) this.showHeaderSubscription.unsubscribe();
        this.sopoPopupService.removeViewContainerRef('app');
    }
}
